/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

// @context
import { useAuth } from '../../context/AuthContext';

// @components
import DashboardNavbar from '../../components/dashboard/DashboardNavbar';
import LeadPriceManager from '../../components/dashboard/LeadPriceManager';
import { PathRoutes } from '../../components/routes/PathRoutes';

// @services
import companyService from '../../services/company.service';
import leadPriceService from '../../services/lead-price.service';
import SubscriptionService from '../../services/subscription.service';
interface Conversation {
  price: string;
  id: string;
  title: string;
  subtitle: string; // Añadimos el subtítulo para el servicio
  type: string;

  userNmaerequest: string;
  serviceDate: string;
  cityName: string;
  originalRequest?: any; // Para guardar la solicitud original completa
}

const CompanyDashboard: React.FC = () => {
  const refetch = useRef<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { userInfo, setUserInfo } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [leadPrice, setLeadPrice] = useState<number>(0);
  const [totalRequestsPrice, setTotalRequestsPrice] = useState<number>(0);
  const [totalPaid, setTotalPaid] = useState<number>(0);
  const companyId = localStorage.getItem('company_id') || localStorage.getItem('user_id');

  const handleGetLeadPrice = async () => {
    const userId = localStorage.getItem('user_id');
    if (userId) {
      try {
        const price = await leadPriceService.getLeadPrice(userId);
        setLeadPrice(price);
        if (userInfo && price !== userInfo.leadPrice) {
          setUserInfo({
            ...userInfo,
            id: userId, // Añadir el ID que faltaba
            leadPrice: price
          });
        }
      } catch (error) {
        toast.error('Esperimentamos problemas al cargar el precio de leads.');
      }
    }
  }

  // Cargar el precio de leads al iniciar
  useEffect(() => {
    const fetchLeadPrice = async () => {
      if (!userInfo || !userInfo.id) {
        handleGetLeadPrice();
      } else {
        try {
          const price = await leadPriceService.getLeadPrice(userInfo.id);
          setLeadPrice(price);
          if (price !== userInfo.leadPrice) setUserInfo({ ...userInfo, leadPrice: price });
        } catch (error) {
          toast.error('Error al cargar el precio de leads.');
        }
      }
    };
    fetchLeadPrice();
  }, [userInfo, setUserInfo]);

  useEffect(() => {
    if (refetch.current) return;
    refetch.current = true;
    const fetchUserServiceRequests = async () => {
      // Intentar obtener el userId del localStorage o del token JWT
      const userToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const userId = localStorage.getItem('user_id');

      if (!userId && !userToken) return setLoading(false);
      try {
        setLoading(true);
        setError(null);
        let userIdToUse = userId;
        // Si aún no tenemos un ID válido, intentar extraerlo del token JWT
        if (!userIdToUse && userToken) {
          try {
            const tokenParts = userToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              // Priorizar el ID interno del backend, no el googleId
              if (payload.id) {
                userIdToUse = payload.id;
              } else if (payload.userId) {
                userIdToUse = payload.userId;
              } else if (payload.sub) {
                userIdToUse = payload.sub;
              }
              if (userIdToUse) localStorage.setItem('user_id', userIdToUse);
            }
          } catch (error) {
          }
        }

        // Si aún no tenemos un ID válido, mostrar error y salir
        if (!userIdToUse) {
          setError('No se pudo obtener el ID de usuario. Por favor, inicia sesión nuevamente.');
          setLoading(false);
          return;
        }
        // Obtener las solicitudes de servicio del usuario
        const serviceRequests = await companyService.getServiceRequests(userIdToUse);
        // Transformar las solicitudes de servicio al formato de conversaciones
        const formattedConversations: Conversation[] = serviceRequests.map(request => {
          // Formatear la fecha relativa (ej: "Hace 2 horas")
          let formattedDate = 'Fecha desconocida';
          if (request.serviceDate) {
            try {
              const date = new Date(request.serviceDate);
              const now = new Date();

              const diffMs = now.getTime() - date.getTime();
              const diffMinutes = Math.floor(diffMs / (1000 * 60));
              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

              // Formatear de manera personalizada
              if (diffMinutes < 1) {
                formattedDate = 'Hace un momento';
              } else if (diffMinutes < 60) {
                formattedDate = `Hace ${diffMinutes} minutos`;
              } else if (diffHours < 24) {
                formattedDate = `Hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
              } else if (diffDays < 30) {
                formattedDate = `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
              } else {
                // Para fechas más antiguas, mostrar la fecha exacta
                formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
              }
            } catch (e) {
              toast.error('Error al formatear la fecha.');
            }
          }
          return {
            id: request.id,
            title: request.serviceTitle || 'General', // Título es la categoría
            subtitle: request.id || 'Solicitud sin nombre', // Subtítulo es el servicio
            type: request.id || 'general', // Mantenemos la categoría sin toLowerCase
            userNmaerequest: request.userName,
            serviceDate: formattedDate,
            cityName: request.cityName,
            price: request.leadPrice,
            originalRequest: request // Guardamos la solicitud original completa
          };
        });
        setConversations(formattedConversations);
        // Calcular el total de precios de todas las solicitudes
        const total = formattedConversations.reduce((sum, conversation) => {
          const price = Number(conversation.price) || 0;
          return sum + price;
        }, 0);
        setTotalRequestsPrice(total);
        setLoading(false);
        toast.success('Solicitudes de servicio cargadas correctamente.');
      } catch (err) {
        toast.error('Error al obtener las solicitudes de servicio, intenta de nuevo más tarde.');
        setError('No se pudieron cargar las solicitudes. Intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };
    fetchUserServiceRequests();
  }, []);

  const getTotalPaid = async () => {
    if (!companyId) return;
    try {
      const response = await SubscriptionService.getTotalPaid(companyId);
      setTotalPaid(response.data.total);
    } catch (error) {
      toast.error('Error al obtener el total pagado, intenta de nuevo más tarde.');
    }
  }

  useEffect(() => {
    getTotalPaid();
  }, []);

  // Mensaje de error que no bloquea la visualización del dashboard
  const ErrorMessage = () => (
    <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
      <p>{error}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark">
      <DashboardNavbar hasSubscription={true} />

      <div className="container mx-auto px-4 py-8">
        {/* Header de la página */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-xervix-text-primary dark:text-xervix-text-primary-dark">Panel de Control</h1>
          <button
            onClick={() => navigate(PathRoutes.promotions)}
            className="bg-xervix-primary hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
            <span>Gestionar Promociones</span>
          </button>
        </div>

        {/* Componente para gestionar el precio de leads */}
        <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark shadow-md rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-xervix-text-primary dark:text-xervix-text-primary-dark">Gestión de Precio por Lead</h2>
          <p className="text-sm mb-4 text-xervix-text-primary dark:text-xervix-text-primary-dark">Precio actual en estado local: {leadPrice !== null ? `${leadPrice} COP` : 'Cargando...'}</p>
          <LeadPriceManager
            initialPrice={leadPrice}
            onPriceUpdated={(newPrice) => {
              setLeadPrice(newPrice);
              if (userInfo) setUserInfo({ ...userInfo, leadPrice: newPrice });
            }}
          />
        </div>

        {error && <ErrorMessage />}

        {/* Cards de información general (Leads y Dinero Pagado) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg shadow-md p-6 text-center">
            <h2 className="text-lg font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">Total de Leads</h2>
            <p className="text-3xl font-bold text-xervix-primary">
              {
                conversations.length || <span className="text-gray-400">Sin datos</span>
              }
            </p>
          </div>

          <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg shadow-md p-6 text-center">
            <h2 className="text-lg font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">Dinero Pagado</h2>
            <p className="text-3xl font-bold text-green-500">
              {loading ? (
                <span className="text-gray-400">Cargando...</span>
              ) :
                <span className="text-green-500">$ {totalPaid ? totalPaid.toLocaleString() : 0}</span>
              }
            </p>
          </div>

          <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg shadow-md p-6 text-center">
            <h2 className="text-lg font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">Dinero por pagar</h2>
            <p className="text-3xl font-bold text-blue-500">
              {loading ? (
                <span className="text-gray-400">Cargando...</span>
              ) :
                <span className="text-gray-400">${totalRequestsPrice.toLocaleString()}</span>
              }
            </p>
          </div>
        </div>

        {/* Tabla de solicitudes de servicio */}
        <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg shadow-md overflow-hidden">
          <h2 className="text-xl font-semibold p-4 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
            Solicitudes de Servicio
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Servicio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ciudad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Precio</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-300">
                      <div className="flex justify-center items-center py-4">
                        <div className="animate-pulse flex space-x-4 w-full max-w-md">
                          <div className="flex-1 space-y-4 py-1">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                            <div className="space-y-2">
                              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : conversations.length > 0 ? (
                  conversations?.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{request.title}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        <div>{request.userNmaerequest}</div>
                        <div className="text-xs text-gray-400">{request.userNmaerequest}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{request.serviceDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{request.cityName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{request.price}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-300">
                      {error ? "Error al cargar los datos" : "No hay solicitudes de servicio disponibles"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};


export default CompanyDashboard;
